/**
 * BUILDING OBSERVE PROVIDER (2026-08-02): the first Observe source — point the
 * unarmed look at any settlement building and read its "sign": what the place
 * is, and who lives or works there (region roster via parcelPeople). Replaces
 * the floating debug house labels.
 */
import type * as THREE from "three";
import { BUILDING_STRIDE, packCell } from "@hyperlight/shared";
import { tangentFrame } from "./buildingMeshes";
import type { Planet } from "../planet";
import type { SettlementRenderer } from "./renderer";
import type { RegionNpcLife } from "../regionNpcLife";
import type { ObserveHit, ObserveProvider } from "../../observe";

/** Short display names, indexed by the append-only USE_* codes (citygraph).
 *  (Index 19, the retired loom gate, keeps its slot — enum values are frozen.) */
export const USE_LABELS: readonly string[] = [
  "HOUSE", "DEPOT", "BUNKHOUSE", "LANDING PAD", "WELL", "HALL", "PRODUCTION",
  "INN", "MARKET SQ", "CHAPEL", "WORKSHOP", "HEADMAN", "WAREHOUSE",
  "MARKET HALL", "TEMPLE", "GUILDHALL", "SMITHY", "GARRISON", "ADMIN",
  "LOOM GATE", "PALACE", "ACADEMY", "HARBOR", "COURTHOUSE", "MONUMENT",
  "STARPORT", "EMBASSY", "PRECURSOR", "BASILICA", "LOOM NEXUS", "SHRINE",
  "MILL", "STABLE", "APOTHECARY", "BATHHOUSE", "THEATER", "ARENA", "DOCKYARD",
  "PRISON", "WATCHTOWER", "FUEL SHED",
];

/** Cap a name list for the panel: first few in full, the rest counted. */
function names(list: string[]): string {
  return list.slice(0, 4).join(", ") + (list.length > 4 ? ` +${list.length - 4} more` : "");
}

export class BuildingObserveProvider implements ObserveProvider {
  /** Renderer/npcLife resolved lazily — both are rebuilt on system change. */
  constructor(private deps: {
    renderer: () => SettlementRenderer | null;
    npcLife: () => RegionNpcLife | null;
  }) { }

  observe(planet: Planet, origin: THREE.Vector3, dir: THREE.Vector3, maxDist: number): ObserveHit | null {
    const renderer = this.deps.renderer();
    if (!renderer) return null;
    // Nearest building whose ORIENTED BOX the ray actually enters — a true
    // ray-OBB slab test (the first cut used fat spheres, which swallowed the
    // whole street around big civic buildings and outbid every citizen).
    // Axes: the record's tangent-frame yaw (long axis), the radial up (height,
    // base 0..h), and their cross (depth).
    let bestT = Infinity;
    let bestUse = -1, bestParcel = -1, bestRk = 0;
    const cullR = maxDist + 40;
    for (const dv of renderer.debugBuildingsFor(planet)) {
      const b = dv.buildings;
      const rk = packCell(dv.cell);
      const n = Math.floor(b.length / BUILDING_STRIDE);
      for (let i = 0; i < n; i++) {
        const o = i * BUILDING_STRIDE;
        const cx = b[o], cy = b[o + 1], cz = b[o + 2];
        const dx = cx - origin.x, dy = cy - origin.y, dz = cz - origin.z;
        if (dx * dx + dy * dy + dz * dz > cullR * cullR) continue;
        const w = b[o + 4], d = b[o + 5], h = b[o + 6], yaw = b[o + 3];
        const f = tangentFrame(cx, cy, cz);
        const cy2 = Math.cos(yaw), sy2 = Math.sin(yaw);
        // long (w) axis, up (h) axis, depth (d) axis
        const ax = f.t1[0] * cy2 + f.t2[0] * sy2, ay = f.t1[1] * cy2 + f.t2[1] * sy2, az = f.t1[2] * cy2 + f.t2[2] * sy2;
        const ux = f.up[0], uy = f.up[1], uz = f.up[2];
        const bx2 = uy * az - uz * ay, by2 = uz * ax - ux * az, bz2 = ux * ay - uy * ax;
        // Ray in box frame: p = origin − base centre.
        const px = origin.x - cx, py = origin.y - cy, pz = origin.z - cz;
        let t0 = 0.5, t1 = Math.min(maxDist, bestT);
        const slab = (p: number, e: number, lo: number, hi: number): boolean => {
          if (Math.abs(e) < 1e-9) return p >= lo && p <= hi;
          let ta = (lo - p) / e, tb = (hi - p) / e;
          if (ta > tb) { const s = ta; ta = tb; tb = s; }
          if (ta > t0) t0 = ta;
          if (tb < t1) t1 = tb;
          return t0 <= t1;
        };
        // Standing INSIDE this building? You can't "observe" the box you're in
        // — skip it, so an indoor citizen (or anything seen through the door)
        // stays readable instead of the shell outbidding everything at 0 m.
        const l1v = px * ax + py * ay + pz * az;
        const l2v = px * ux + py * uy + pz * uz;
        const l3v = px * bx2 + py * by2 + pz * bz2;
        if (Math.abs(l1v) <= w / 2 && l2v >= 0 && l2v <= h && Math.abs(l3v) <= d / 2) continue;
        if (!slab(l1v, dir.x * ax + dir.y * ay + dir.z * az, -w / 2, w / 2)) continue;
        if (!slab(l2v, dir.x * ux + dir.y * uy + dir.z * uz, 0, h)) continue;
        if (!slab(l3v, dir.x * bx2 + dir.y * by2 + dir.z * bz2, -d / 2, d / 2)) continue;
        bestT = t0;
        bestUse = Math.round(b[o + 15]);
        bestParcel = Math.round(b[o + 16]);
        bestRk = rk;
      }
    }
    if (bestUse < 0) return null;

    const label = USE_LABELS[bestUse] ?? "STRUCTURE";
    const people = this.deps.npcLife()?.parcelPeople(planet.def.seed, bestRk, bestParcel) ?? null;
    let title = label;
    let subtitle: string | undefined;
    const lines: string[] = [];
    if (people) {
      // A lived-in house signs itself with the household name.
      if (bestUse === 0 && people.residents.length > 0) {
        const surname = people.residents[0].split(" ").pop();
        if (surname) { title = `${surname} residence`; subtitle = "dwelling"; }
      }
      if (people.residents.length > 0) lines.push(`Residents — ${names(people.residents)}`);
      if (people.workers.length > 0) lines.push(`Staff — ${names(people.workers)}`);
      if (lines.length === 0) lines.push("Unoccupied");
    }
    return { dist: bestT, info: { title, subtitle, lines } };
  }
}
